import React, { useContext, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiLock, FiPackage, FiShield } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/checkout.css";

export const checkoutCountries = [
  "Australia", "Austria", "Belgium", "Canada", "Denmark", "Finland",
  "France", "Germany", "Greece", "Iceland", "Ireland",
  "Italy", "Japan", "Luxembourg", "Macao SAR", "Malaysia", "Netherlands",
  "New Zealand", "Norway", "Portugal", "Singapore", "South Korea", "Spain",
  "Sweden", "Switzerland", "Taiwan", "Thailand", "United Arab Emirates",
  "United Kingdom", "United States", "Vietnam",
];

export const buildPaymentUrl = ({ origin, orderNo, amount, currency, email, title }) => {
  const paymentUrl = new URL("/create_payment.php", origin);
  paymentUrl.search = new URLSearchParams({
    orderNo, amount, currency, email,
    success_url: `${origin}/payment?state=success`,
    cancel_url: `${origin}/payment?state=cancel`,
    title,
  }).toString();
  return paymentUrl.toString();
};

const initialForm = {
  email: "", firstName: "", lastName: "", company: "", address: "",
  apartment: "", city: "", region: "", postalCode: "",
  country: "United Kingdom", acceptedTerms: false,
};

const formatPrice = (price) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: siteConfig.currency,
}).format(Number(price) || 0);

const createOrderNumber = () => {
  const randomValues = new Uint32Array(3);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(randomValues);
  else randomValues.set([Date.now(), Math.random() * 1e9, Math.random() * 1e9]);
  return `${Date.now()}${Array.from(randomValues, (value) => String(value).padStart(10, "0")).join("")}`.slice(0, 30);
};

const CheckOut = () => {
  const formRef = useRef(null);
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const detailedItems = useMemo(() => cartItems.map((item) => ({
    ...item,
    product: productData.find((product) => product.id === Number(item.id)),
  })).filter((item) => item.product), [cartItems]);

  const subtotal = detailedItems.reduce(
    (sum, item) => sum + Number(item.product.price) * Number(item.quantity), 0
  );
  const discount = couponStatus === "applied" ? subtotal * 0.1 : 0;
  const shipping = siteConfig.shippingFlatRate;
  const total = subtotal - discount + shipping;

  const updateField = (event) => {
    const { checked, name, type, value } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const requiredFieldProps = (name, errorId) => ({
    required: true,
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? errorId : undefined,
  });

  const validateForm = () => {
    const nextErrors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) nextErrors.email = "Enter a valid email address.";
    if (!form.firstName.trim()) nextErrors.firstName = "Enter a first name.";
    if (!form.lastName.trim()) nextErrors.lastName = "Enter a last name.";
    if (!checkoutCountries.includes(form.country)) nextErrors.country = "Choose an available delivery country.";
    if (!form.address.trim()) nextErrors.address = "Enter a street address.";
    if (!form.city.trim()) nextErrors.city = "Enter a city.";
    if (!form.region.trim()) nextErrors.region = "Enter a state or region.";
    if (!form.postalCode.trim()) nextErrors.postalCode = "Enter a postal code.";
    if (!form.acceptedTerms) nextErrors.acceptedTerms = "Accept the terms to continue.";
    return nextErrors;
  };

  const handleCoupon = (event) => {
    event.preventDefault();
    if (couponStatus === "applied") {
      setCouponCode(""); setCouponStatus("idle"); return;
    }
    setCouponStatus(couponCode.trim().toUpperCase() === "WILLOW10" ? "applied" : "invalid");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      window.requestAnimationFrame(() => {
        const field = formRef.current?.querySelector("[aria-invalid='true']");
        field?.focus();
        field?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    setIsSubmitting(true);
    const orderNo = createOrderNumber();
    const origin = window.location.origin;
    const pendingOrder = {
      email: form.email.trim(), orderNo, amount: total.toFixed(2),
      currency: siteConfig.currency, title: detailedItems[0].product.name,
    };
    try { window.sessionStorage.setItem("rumnwillow-pending-order-v1", JSON.stringify(pendingOrder)); } catch {}

    window.location.assign(buildPaymentUrl({ origin, orderNo, amount: pendingOrder.amount,
      currency: pendingOrder.currency, email: pendingOrder.email, title: pendingOrder.title }));
  };

  if (!detailedItems.length) return (
    <main className="checkout-empty">
      <span>Checkout</span><h1>Your bag is empty.</h1>
      <p>Add a product to your bag before continuing to checkout.</p>
      <Link to="/shop">Return to shop</Link>
    </main>
  );

  return (
    <main className="checkout-page">
      <header className="checkout-heading">
        <Link to="/" className="checkout-wordmark">{siteConfig.brandName}</Link>
        <div className="checkout-steps" aria-label="Checkout steps">
          <span className="is-active">Information</span><span>Review</span><span>Payment</span>
        </div>
        <p><FiLock aria-hidden="true" /> Secure checkout</p>
      </header>
      <h1 className="visually-hidden" id="checkout-title">Checkout</h1>

      <div className="checkout-layout">
        <section className="checkout-form-column" aria-labelledby="checkout-contact-title">
          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            <section className="checkout-section">
              <div className="checkout-section__heading"><span>01</span><h2 id="checkout-contact-title">Contact</h2></div>
              <label className="checkout-field checkout-field--full" htmlFor="checkout-email">
                <span>Email address</span>
                <input id="checkout-email" name="email" type="email" value={form.email} onChange={updateField} autoComplete="email" placeholder="you@example.com" {...requiredFieldProps("email", "checkout-email-error")} />
                {errors.email && <small id="checkout-email-error" className="checkout-error" role="alert">{errors.email}</small>}
              </label>
            </section>

            <section className="checkout-section">
              <div className="checkout-section__heading"><span>02</span><h2>Delivery address</h2></div>
              <div className="checkout-fields">
                <label className="checkout-field checkout-field--full" htmlFor="checkout-country">
                  <span>Country / region</span>
                  <select id="checkout-country" name="country" value={form.country} onChange={updateField} autoComplete="country-name" aria-invalid={Boolean(errors.country)} required>
                    {checkoutCountries.map((country) => <option key={country}>{country}</option>)}
                  </select>
                  {errors.country && <small className="checkout-error" role="alert">{errors.country}</small>}
                </label>
                <label className="checkout-field" htmlFor="checkout-first-name"><span>First name</span><input id="checkout-first-name" name="firstName" value={form.firstName} onChange={updateField} autoComplete="given-name" {...requiredFieldProps("firstName", "checkout-first-name-error")} />{errors.firstName && <small id="checkout-first-name-error" className="checkout-error" role="alert">{errors.firstName}</small>}</label>
                <label className="checkout-field" htmlFor="checkout-last-name"><span>Last name</span><input id="checkout-last-name" name="lastName" value={form.lastName} onChange={updateField} autoComplete="family-name" {...requiredFieldProps("lastName", "checkout-last-name-error")} />{errors.lastName && <small id="checkout-last-name-error" className="checkout-error" role="alert">{errors.lastName}</small>}</label>
                <label className="checkout-field checkout-field--full" htmlFor="checkout-company"><span>Company <small>Optional</small></span><input id="checkout-company" name="company" value={form.company} onChange={updateField} autoComplete="organization" /></label>
                <label className="checkout-field checkout-field--full" htmlFor="checkout-address"><span>Address</span><input id="checkout-address" name="address" value={form.address} onChange={updateField} autoComplete="address-line1" {...requiredFieldProps("address", "checkout-address-error")} />{errors.address && <small id="checkout-address-error" className="checkout-error" role="alert">{errors.address}</small>}</label>
                <label className="checkout-field checkout-field--full" htmlFor="checkout-apartment"><span>Apartment, suite, etc. <small>Optional</small></span><input id="checkout-apartment" name="apartment" value={form.apartment} onChange={updateField} autoComplete="address-line2" /></label>
                <label className="checkout-field" htmlFor="checkout-city"><span>City</span><input id="checkout-city" name="city" value={form.city} onChange={updateField} autoComplete="address-level2" {...requiredFieldProps("city", "checkout-city-error")} />{errors.city && <small id="checkout-city-error" className="checkout-error" role="alert">{errors.city}</small>}</label>
                <label className="checkout-field" htmlFor="checkout-region"><span>State / region</span><input id="checkout-region" name="region" value={form.region} onChange={updateField} autoComplete="address-level1" {...requiredFieldProps("region", "checkout-region-error")} />{errors.region && <small id="checkout-region-error" className="checkout-error" role="alert">{errors.region}</small>}</label>
                <label className="checkout-field" htmlFor="checkout-postal-code"><span>Postal code</span><input id="checkout-postal-code" name="postalCode" value={form.postalCode} onChange={updateField} autoComplete="postal-code" {...requiredFieldProps("postalCode", "checkout-postal-code-error")} />{errors.postalCode && <small id="checkout-postal-code-error" className="checkout-error" role="alert">{errors.postalCode}</small>}</label>
              </div>
            </section>

            <section className="checkout-section">
              <div className="checkout-section__heading"><span>03</span><h2>Shipping method</h2></div>
              <div className="checkout-shipping-options">
                <label className="is-selected"><input type="radio" checked readOnly /><span className="checkout-radio" aria-hidden="true" /><span><strong>Tracked shipping</strong><small>Estimated timing shown for the selected destination</small></span><strong>{formatPrice(shipping)}</strong></label>
              </div>
            </section>

            <section className="checkout-section">
              <div className="checkout-section__heading"><span>04</span><h2>Payment</h2></div>
              <div className="checkout-demo-note"><FiShield aria-hidden="true" /><p><strong>Secure hosted payment</strong> After you click Pay now, you will continue to our payment page. We do not collect card details on this form.</p></div>
              <div className="checkout-payment-box"><div className="checkout-payment-box__head"><span><span className="checkout-radio" aria-hidden="true" /> Supported payment methods</span><span aria-label="Accepted payment methods">VISA · MC · AMEX · APPLE PAY · GOOGLE PAY</span></div></div>
              <label className="checkout-check checkout-terms"><input name="acceptedTerms" type="checkbox" checked={form.acceptedTerms} onChange={updateField} {...requiredFieldProps("acceptedTerms", "checkout-terms-error")} /><span>I agree to the <Link to="/terms">terms</Link> and acknowledge the <Link to="/privacy">privacy policy</Link>.</span></label>
              {errors.acceptedTerms && <small id="checkout-terms-error" className="checkout-error checkout-terms-error" role="alert">{errors.acceptedTerms}</small>}
              <button type="submit" className="checkout-submit" disabled={isSubmitting}>{isSubmitting ? "Opening secure payment…" : `Pay now · ${formatPrice(total)}`}{!isSubmitting && <FiLock aria-hidden="true" />}</button>
              <p className="checkout-submit-note">You will be redirected to the secure payment service to complete your order.</p>
            </section>
          </form>
        </section>

        <aside className="checkout-summary" aria-labelledby="checkout-summary-title"><div className="checkout-summary__inner">
          <div className="checkout-summary__heading"><h2 id="checkout-summary-title">Order summary</h2><Link to="/cartpage">Edit bag</Link></div>
          <div className="checkout-summary__items">{detailedItems.map((item) => <article key={`${item.id}-${item.size}-${item.color}`}><div className="checkout-summary__image"><img src={item.product.images[0]} alt={item.product.name} /><span>{item.quantity}</span></div><div><h3>{item.product.name}</h3><p>{item.size} · {item.color}</p></div><strong>{formatPrice(Number(item.product.price) * Number(item.quantity))}</strong></article>)}</div>
          <form className="checkout-coupon" onSubmit={handleCoupon}><label className="visually-hidden" htmlFor="checkout-coupon">Discount code</label><input id="checkout-coupon" value={couponCode} onChange={(event) => { setCouponCode(event.target.value); setCouponStatus("idle"); }} placeholder="Discount code" disabled={couponStatus === "applied"} /><button type="submit" disabled={!couponCode.trim() && couponStatus !== "applied"}>{couponStatus === "applied" ? "Remove" : "Apply"}</button><small className={couponStatus} aria-live="polite">{couponStatus === "applied" && "WILLOW10 applied: 10% off"}{couponStatus === "invalid" && "That code is not valid. Try WILLOW10"}{couponStatus === "idle" && "Try WILLOW10"}</small></form>
          <dl className="checkout-summary__totals"><div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>{discount > 0 && <div className="checkout-discount"><dt>Discount</dt><dd>−{formatPrice(discount)}</dd></div>}<div><dt>Shipping</dt><dd>{formatPrice(shipping)}</dd></div><div className="checkout-summary__total"><dt>Total</dt><dd><small>{siteConfig.currency}</small> {formatPrice(total)}</dd></div></dl>
          <div className="checkout-assurance"><FiPackage aria-hidden="true" /><p><strong>Prepared with care</strong>Orders typically leave our studio in {siteConfig.policies.dispatchTime}.</p></div>
        </div></aside>
      </div>
    </main>
  );
};

export default CheckOut;
